import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button, Form, Input, InputNumber, Modal, Segmented, Select, Table, Tag, Typography,
} from 'antd';
import { PercentageOutlined } from '@ant-design/icons';
import type { TableColumnsType } from 'antd';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useFeedback } from '../utils/feedback';
import { getApiErrorMessage } from '../utils/apiError';
import { formatDateEuropean } from '../utils/dateUtils';
import { warningCopy } from '../modules/operationalExceptions/operationalExceptionText';
import {
  OPERATIONAL_EXCEPTION_CODES,
  fetchConsistencyWarningSettings,
  fetchOperationalExceptions,
  operationalExceptionDocumentPath,
  resolveOperationalException,
  saveConsistencyWarningSettings,
  type OperationalExceptionDto,
  type OperationalExceptionStatusFilter,
} from '../modules/operationalExceptions/operationalExceptionsApi';
import '../components/layout/AppPage.css';
import { ObjectCardSection } from '../components/layout/ObjectCard';
import '../components/layout/ObjectCardExtras.css';
import './OperationalExceptions.css';

/**
 * The registry shows the very sentence the operator confirmed in the dialog:
 * both read `operationalExceptionText.ts`. A code this build does not know
 * still reads as a sentence, with the raw code only as a grey caption.
 */
const ExceptionDetails: React.FC<{ row: OperationalExceptionDto; t: TFunction; full?: boolean }> = ({ row, t, full }) => {
  const copy = warningCopy({ code: row.warningCode, details: row.details }, t);
  const support = copy.supportCode ? t('common:operationalException.supportCode', { code: copy.supportCode }) : null;
  if (!full) {
    // One line in the row; the expanded row below shows the whole sentence.
    return (
      <div className="exceptions-cell">
        <span className="exceptions-cell__line" title={copy.text}>{copy.text}</span>
        {support && <span className="exceptions-cell__meta" title={support}>{support}</span>}
      </div>
    );
  }
  return (
    <div className="exceptions-cell">
      <span className="exceptions-cell__text">{copy.text}</span>
      {support && <span className="exceptions-cell__meta">{support}</span>}
    </div>
  );
};

/**
 * Administrator list of confirmed operational exceptions (decision
 * 2026-09-18): every warning the operator continued through, with the
 * reason, author and document link. An entry stays "open" until an
 * administrator marks it as explained with a comment.
 */
// WP2: WP1 "needs re-settlement" entries. A correction invoice closes them;
// the administrator can close one by hand when no correction is needed.
const RESETTLEMENT_CODES = new Set([
  'ORDER_CHANGED_AFTER_INVOICE',
  'WZ_CORRECTED_AFTER_INVOICE',
  'SUPPLIER_ORDER_CHANGED_AFTER_INVOICE',
  'SUPPLIER_ORDER_PZ_QUANTITY_REDUCED',
  'ORDER_ISSUED_OVER_INVOICE',
]);

const OperationalExceptions: React.FC = () => {
  const { t } = useTranslation(['common']);
  const feedback = useFeedback();
  const [rows, setRows] = useState<OperationalExceptionDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<OperationalExceptionStatusFilter>('open');
  const [warningCode, setWarningCode] = useState<string | null>(null);
  const [resolving, setResolving] = useState<OperationalExceptionDto | null>(null);
  const [resolveSubmitting, setResolveSubmitting] = useState(false);
  const [resolveForm] = Form.useForm<{ comment: string }>();
  const [threshold, setThreshold] = useState<number | null>(null);
  const [thresholdSaving, setThresholdSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await fetchOperationalExceptions({ status, warningCode }));
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'common:operationalExceptionsAdmin.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [status, warningCode, feedback, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetchConsistencyWarningSettings()
      .then((settings) => setThreshold(settings.purchasePriceDeviationPercent))
      .catch((error) => feedback.error(getApiErrorMessage(error, t, 'common:operationalExceptionsAdmin.settings.fetchError')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const codeLabel = (code: string) => {
    // A missing label comes back as the key without its namespace.
    const key = `operationalExceptionsAdmin.codes.${code}`;
    const label = t(`common:${key}`);
    return label === key || label === `common:${key}` ? code : label;
  };

  const handleResolve = async () => {
    if (!resolving) return;
    try {
      const values = await resolveForm.validateFields();
      setResolveSubmitting(true);
      await resolveOperationalException(resolving.id, values.comment.trim());
      feedback.success(t('common:operationalExceptionsAdmin.resolveSuccess'));
      setResolving(null);
      resolveForm.resetFields();
      await load();
    } catch (error: any) {
      if (error?.errorFields) return;
      feedback.error(getApiErrorMessage(error, t, 'common:operationalExceptionsAdmin.resolveError'));
    } finally {
      setResolveSubmitting(false);
    }
  };

  const handleSaveThreshold = async () => {
    if (threshold === null) return;
    setThresholdSaving(true);
    try {
      const saved = await saveConsistencyWarningSettings(threshold);
      setThreshold(saved.purchasePriceDeviationPercent);
      feedback.success(t('common:operationalExceptionsAdmin.settings.saveSuccess', { percent: saved.purchasePriceDeviationPercent }));
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'common:operationalExceptionsAdmin.settings.saveError'));
    } finally {
      setThresholdSaving(false);
    }
  };

  // One line per value (DESIGN.md „Direction since 2026-10-19”): each cell is
  // a title line and a muted meta line, cut with an ellipsis and the full text
  // in `title`. A click on the row opens the whole sentence the operator
  // confirmed, with the reason and the explanation.
  const columns: TableColumnsType<OperationalExceptionDto> = [
    {
      title: t('common:operationalExceptionsAdmin.columns.code'),
      key: 'entry',
      width: 260,
      render: (_: unknown, row) => {
        const path = operationalExceptionDocumentPath(row);
        const label = t(`common:operationalExceptionsAdmin.scopes.${row.scopeType}`);
        const code = codeLabel(row.warningCode);
        return (
          <div className="exceptions-cell">
            <span className="exceptions-cell__line exceptions-cell__name" title={code}>{code}</span>
            {/* The document label gives way; the date always stays whole. */}
            <span className="exceptions-cell__meta exceptions-cell__meta--dated" title={`${label} · ${formatDateEuropean(row.createdAt)}`}>
              <span className="exceptions-cell__scope">
                {path
                  ? <Link to={path} onClick={(event) => event.stopPropagation()}>{label}</Link>
                  : label}
              </span>
              <span className="exceptions-cell__date">
                <span aria-hidden="true">{' \u00b7 '}</span>
                {formatDateEuropean(row.createdAt)}
              </span>
            </span>
          </div>
        );
      },
    },
    {
      title: t('common:operationalExceptionsAdmin.columns.details'),
      key: 'details',
      render: (_: unknown, row) => <ExceptionDetails row={row} t={t} />,
    },
    {
      title: t('common:operationalExceptionsAdmin.columns.reason'),
      key: 'reason',
      width: 220,
      render: (_: unknown, row) => (
        <div className="exceptions-cell">
          <span className="exceptions-cell__line" title={row.reason}>{row.reason}</span>
          <span className="exceptions-cell__meta" title={row.confirmedByName || undefined}>{row.confirmedByName}</span>
        </div>
      ),
    },
    {
      title: t('common:operationalExceptionsAdmin.columns.resolution'),
      key: 'resolution',
      width: 220,
      render: (_: unknown, row) => (row.resolutionId ? (
        <div className="exceptions-cell">
          {row.settledByInvoice ? (
            <Tag color="green" className="exceptions-cell__tag">
              {t('common:operationalExceptionsAdmin.settledByCorrection', { number: row.settledByInvoiceNumber || '' })}
            </Tag>
          ) : (
            <span className="exceptions-cell__line" title={row.resolutionComment || ''}>{row.resolutionComment}</span>
          )}
          <span className="exceptions-cell__meta">
            {t('common:operationalExceptionsAdmin.resolvedBy', {
              name: row.resolvedByName || '',
              date: formatDateEuropean(row.resolvedAt),
            })}
          </span>
        </div>
      ) : (
        <Button size="small" onClick={(event) => { event.stopPropagation(); resolveForm.resetFields(); setResolving(row); }}>
          {t('common:operationalExceptionsAdmin.resolve')}
        </Button>
      )),
    },
  ];

  const renderExpanded = (row: OperationalExceptionDto) => (
    <dl className="exceptions-expanded">
      <div>
        <dt>{t('common:operationalExceptionsAdmin.columns.details')}</dt>
        <dd><ExceptionDetails row={row} t={t} full /></dd>
      </div>
      <div>
        <dt>{t('common:operationalExceptionsAdmin.columns.reason')}</dt>
        <dd className="exceptions-cell__text">{row.reason}</dd>
      </div>
      {row.resolutionId && (
        <div>
          <dt>{t('common:operationalExceptionsAdmin.columns.resolution')}</dt>
          <dd className="exceptions-cell__text">{row.resolutionComment}</dd>
        </div>
      )}
    </dl>
  );

  return (
    <div className="app-page exceptions-page">
      <section className="app-page__card">
        <div className="app-page__toolbar">
          <div className="app-page__toolbar-group">
            <Segmented<OperationalExceptionStatusFilter>
              value={status}
              onChange={setStatus}
              options={[
                { value: 'open', label: t('common:operationalExceptionsAdmin.filters.open') },
                { value: 'resolved', label: t('common:operationalExceptionsAdmin.filters.resolved') },
                { value: 'all', label: t('common:operationalExceptionsAdmin.filters.all') },
              ]}
              aria-label={t('common:operationalExceptionsAdmin.filters.status')}
            />
            <Select<string | null>
              className="exceptions-page__code-filter"
              value={warningCode}
              onChange={(value) => setWarningCode(value || null)}
              allowClear
              placeholder={t('common:operationalExceptionsAdmin.filters.allCodes')}
              options={OPERATIONAL_EXCEPTION_CODES.map((code) => ({ value: code, label: codeLabel(code) }))}
              aria-label={t('common:operationalExceptionsAdmin.filters.code')}
            />
          </div>
          <span className="app-page__count" aria-live="polite">{rows.length}</span>
        </div>
        <div className="app-page__card-intro">
          <Typography.Text type="secondary">{t('common:operationalExceptionsAdmin.description')}</Typography.Text>
        </div>
        <Table<OperationalExceptionDto>
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={{ pageSize: 25, hideOnSinglePage: true }}
          tableLayout="fixed"
          scroll={{ x: 960 }}
          expandable={{ expandedRowRender: renderExpanded, expandRowByClick: true }}
          rowClassName={() => 'exceptions-row'}
          locale={{ emptyText: t('common:operationalExceptionsAdmin.empty') }}
        />
      </section>

      <ObjectCardSection
        title={t('common:operationalExceptionsAdmin.settings.title')}
        icon={<PercentageOutlined />}
        tone="orange"
        hint={t('common:operationalExceptionsAdmin.settings.description')}
        actions={(
          <Button type="primary" size="small" loading={thresholdSaving} disabled={threshold === null} onClick={handleSaveThreshold}>
            {t('common:operationalExceptionsAdmin.settings.save')}
          </Button>
        )}
      >
        <Form layout="vertical" component="div">
          <div className="obj-form">
            <Form.Item className="tool-form__end" label={t('common:operationalExceptionsAdmin.settings.threshold')}>
              <InputNumber
                className="exceptions-page__threshold"
                min={0}
                max={1000}
                value={threshold}
                addonAfter="%"
                onChange={(value) => setThreshold(typeof value === 'number' ? value : null)}
                aria-label={t('common:operationalExceptionsAdmin.settings.threshold')}
              />
            </Form.Item>
          </div>
        </Form>
      </ObjectCardSection>

      <Modal
        open={Boolean(resolving)}
        title={t('common:operationalExceptionsAdmin.resolveTitle')}
        okText={t('common:operationalExceptionsAdmin.resolve')}
        cancelText={t('common:cancel')}
        confirmLoading={resolveSubmitting}
        onOk={handleResolve}
        onCancel={() => { setResolving(null); resolveForm.resetFields(); }}
        destroyOnClose
      >
        <Form form={resolveForm} layout="vertical">
          <Form.Item
            name="comment"
            label={t('common:operationalExceptionsAdmin.resolveComment')}
            rules={[{ required: true, whitespace: true, message: t('common:operationalExceptionsAdmin.resolveCommentRequired') }]}
          >
            <Input.TextArea rows={3} maxLength={500} showCount placeholder={t('common:operationalExceptionsAdmin.resolveCommentPlaceholder')} />
          </Form.Item>
          {resolving && RESETTLEMENT_CODES.has(resolving.warningCode) && (
            <div className="exceptions-cell exceptions-cell--hint">
              <Typography.Text type="secondary">{t('common:operationalExceptionsAdmin.resettlementHint')}</Typography.Text>
              <Button size="small" onClick={() => resolveForm.setFieldsValue({ comment: t('common:operationalExceptionsAdmin.noCorrectionNeeded') })}>
                {t('common:operationalExceptionsAdmin.noCorrectionNeeded')}
              </Button>
            </div>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default OperationalExceptions;
